import React, { useRef, useState } from 'react';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { trackMetaEvent } from '../utils/metaPixel';
interface FormState {
  nombre: string;
  telefono: string;
  correo: string;
  modalidad: string;
}
const initialState: FormState = {
  nombre: '',
  telefono: '',
  correo: '',
  modalidad: 'Presencial'
};
const fieldClass = 'w-full border border-ink/15 bg-white px-4 py-4 font-sans text-base text-ink transition-colors duration-200 ease-out hover:border-champagne/70 focus:border-champagne focus:outline-none focus:ring-1 focus:ring-champagne/40';
const labelClass = 'block font-sans text-[11px] font-medium uppercase tracking-[0.18em] text-steel';
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xdeopzbz';
export function LeadForm() {
  const [form, setForm] = useState<FormState>(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const leadTracked = useRef(false);
  const update = (key: keyof FormState, value: string) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: new FormData(formEl),
        headers: {
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        if (!leadTracked.current) {
          leadTracked.current = true;
          trackMetaEvent('Lead');
        }
        formEl.reset();
        setForm(initialState);
        setSubmitted(true);
      } else {
        const details = await response.text();
        console.error('Formspree error response:', response.status, details);
        setError('No pudimos enviar tu información. Por favor, inténtalo nuevamente.');
      }
    } catch (err) {
      console.error('Formspree request failed:', err);
      setError('No pudimos enviar tu información. Por favor, inténtalo nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };
  return <section id="formulario" className="w-full scroll-mt-20 bg-ivory-dark py-20 sm:py-28 lg:py-36" aria-labelledby="form-title" data-id="element-104" data-mp="2hw7ddo4n3r69">
      <div className="mx-auto max-w-page px-5 sm:px-10 lg:px-12" data-id="element-105" data-mp="2cyp1ppz8mxuk">
        {submitted ? <div className="mx-auto max-w-2xl border border-ink/10 bg-white px-6 py-16 text-center shadow-[0_18px_60px_-40px_rgba(11,34,49,0.45)] sm:px-12 sm:py-20" data-id="element-106" data-mp="285m7dfmmzkdb">
            <span className="mx-auto flex h-14 w-14 items-center justify-center border border-champagne" data-id="element-107" data-mp="1y8q71x6w64ju">
              <CheckIcon className="h-6 w-6 text-champagne" strokeWidth={1.5} data-id="element-108" data-mp="1u74phyjex23b" />
            </span>
            <h2 className="mt-10 font-sans text-3xl font-light uppercase leading-tight tracking-[0.02em] text-ink sm:text-[2.5rem]" data-id="element-109" data-mp="1p706o1v17v9x">
              ¡Gracias!
            </h2>
            <p className="mx-auto mt-6 max-w-xl font-sans text-[17px] font-light leading-relaxed text-steel sm:text-lg" data-id="element-110" data-mp="9okd89hksorp">
              Recibimos tus datos. Nos pondremos en contacto contigo para
              coordinar la conversación y responder tus preguntas.
            </p>
          </div> : <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-20" data-id="element-111" data-mp="3i4nkwf8oudg1">
            <Reveal data-id="element-112" data-mp="3m5pm31m60zkn">
              <h2 id="form-title" className="font-sans text-[1.9rem] font-light uppercase leading-[1.1] text-ink sm:text-[2.6rem] xl:text-[3rem]" data-id="element-113" data-mp="3m57jln8wtp4a">
                Coordinemos una
                <br data-id="element-114" data-mp="3vxd64hge7ec3" />
                conversación.
              </h2>
              <p className="mt-6 font-serif text-2xl italic leading-snug text-champagne sm:text-[1.75rem]" data-id="element-115" data-mp="2yh6c8hngweqg">
                Conocer puede ser el primer paso hacia algo nuevo.
              </p>
              <p className="mt-7 max-w-lg font-sans text-[17px] font-light leading-relaxed text-steel sm:text-lg" data-id="element-116" data-mp="19w8vs8mj8bk6">
                Déjanos tus datos y coordinaremos contigo un encuentro para que
                conozcas cómo funciona y puedas hacer todas tus preguntas.
              </p>

              <div className="mt-12 border-t border-ink/12 pt-8" data-id="element-117" data-mp="1dym0qutgg5fw">
                <p className="font-sans text-[11px] font-medium uppercase tracking-[0.18em] text-champagne" data-id="element-118" data-mp="1dw9dq38ub4vz">
                  Encuentro presencial
                </p>
                <p className="mt-3 font-sans text-base font-light text-ink" data-id="element-119" data-mp="1nndrx0qoylpr">
                  Miami, FL 33172
                </p>
                <p className="mt-4 max-w-md font-sans text-sm font-light leading-relaxed text-steel" data-id="element-120" data-mp="21lu61cqvvehx">
                  Si no estás cerca de esta zona, podemos coordinar un punto de
                  encuentro que funcione para ambas partes.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.08} data-id="element-121" data-mp="3aojoblo04pq">
              <div className="rounded-md border border-ink/10 bg-white p-6 shadow-[0_18px_60px_-40px_rgba(11,34,49,0.45)] sm:p-10" data-id="element-122" data-mp="3p99hfrpsk1tx">
                <form action={FORMSPREE_ENDPOINT} method="POST" onSubmit={handleSubmit} className="space-y-7" data-id="element-123" data-mp="3tauyzqd9t4ag">
                  <input type="hidden" name="modalidad" value={form.modalidad} data-id="element-124" data-mp="3khhx3yx8asc8" />

                  <div data-id="element-125" data-mp="3alems5s83ne7">
                    <label htmlFor="nombre" className={labelClass} data-id="element-126" data-mp="3alocrtd9pujw">
                      Nombre y apellido *
                    </label>
                    <input id="nombre" name="nombre" type="text" required autoComplete="name" value={form.nombre} onChange={e => update('nombre', e.target.value)} className={`mt-3 ${fieldClass}`} data-id="element-127" data-mp="2wn6qm030gq43" />
                  </div>

                  <div data-id="element-128" data-mp="tctq0tx1vfr3">
                    <label htmlFor="telefono" className={labelClass} data-id="element-129" data-mp="tcbuy0m4j7kr">
                      Teléfono *
                    </label>
                    <input id="telefono" name="telefono" type="tel" required autoComplete="tel" value={form.telefono} onChange={e => update('telefono', e.target.value)} className={`mt-3 ${fieldClass}`} data-id="element-130" data-mp="oe0kbno08t1h" />
                  </div>

                  <div data-id="element-131" data-mp="17a080m9y9af9">
                    <label htmlFor="correo" className={labelClass} data-id="element-132" data-mp="12drwrjyvi1dd">
                      Correo electrónico *
                    </label>
                    <input id="correo" name="email" type="email" required autoComplete="email" value={form.correo} onChange={e => update('correo', e.target.value)} className={`mt-3 ${fieldClass}`} data-id="element-133" data-mp="1zu8h18gr9a58" />
                  </div>

                  <div className="pt-2" data-id="element-134" data-mp="2cdchp6cy80fv">
                    <p className="font-sans text-sm font-light text-steel" data-id="element-135" data-mp="2havxiapaol9x">
                      Sin compromiso. Primero conoce. Después decide.
                    </p>

                    <button type="submit" disabled={submitting} className="group mt-5 inline-flex w-full items-center justify-center gap-3 bg-ink px-8 py-5 font-sans text-xs font-medium uppercase tracking-[0.2em] text-ivory transition-colors duration-200 ease-out hover:bg-ink-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:cursor-not-allowed disabled:opacity-60" data-id="element-136" data-mp="2la3jlze6m9id">
                      {submitting ? 'Enviando...' : 'Agendar una conversación'}
                      {!submitting && <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-137" data-mp="3q9u5519mis1l" />}
                    </button>

                    {error && <p role="alert" className="mt-4 font-sans text-sm font-light leading-relaxed text-ink" data-id="element-138" data-mp="3bmr80vp7qles">
                        {error}
                      </p>}

                    <p className="mt-4 font-sans text-sm font-light leading-relaxed text-steel" data-id="element-139" data-mp="3glc5ind2yqzj">
                      Te contactaremos para coordinar el día, horario y
                      ubicación.
                    </p>
                  </div>
                </form>
              </div>
            </Reveal>
          </div>}
      </div>
    </section>;
}